// src/containers/ListingPage/ListingPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';
import { createResourceLocatorString } from '../../util/routes';
import { parse } from '../../util/urlHelpers';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_INITIATE_TRANSACTIONS,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import {
  hasPermissionToViewData,
  hasPermissionToInitiateTransactions,
  isUserAuthorized,
} from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  LayoutSingleColumn,
  AuctionBuyBox,
  OrderPanel,
} from '../../components';

// Related containers
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  submitBidTransaction,
  sendOffer,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';

import ActionBarMaybe from './ActionBarMaybe';
import SectionGallery from './SectionGallery';

import css from './ListingPage.module.css';
import { SHIPPING } from '../../util/constants.js';
import { moneyFromBidInput } from '../../util/bidPricing';

import BidModal from './BidModal';
import Breadcrumbs from './Breadcrumbs';
import ContentTabs from './ContentTabs';
import SimilarListings from './SimilarListings';

import {
  titleCaseFromSlug,
  formatUnixToDate,
  getEnumLabelFromListingFields,
  getFilterParamNameForListingField,
} from './helpers';

import OrderPanelCtaInfo from './OrderPanelCtaInfo/OrderPanelCtaInfo';
import PriceMaybe from './PriceMaybe';
import { BooleanEnum } from '../../util/enums.js';
import { normalizeMoney, showToaster } from '../../util/genericHelpers.js';
import { getTimedSaleEndUnix, isActiveTimedSaleListing } from '../../util/timedSales';
import OfferModal from '../OfferModal/OfferModal.js';
import { formatListingAge } from '../../util/listingRules';
import { getListingBidQuantity } from '../../util/bidPricing';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const { UUID } = sdkTypes;
const MODAL_BREAKPOINT = 1023;

const getCountdownParts = targetMs => {
  const diff = Math.max(0, targetMs - Date.now());
  const totalSec = Math.floor(diff / 1000);
  const days = Math.floor(totalSec / 86400);
  const hrs = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  return { days, hrs, mins, secs, isEnded: diff === 0 };
};

const maskBidderName = (name, fallbackId) => {
  if (name) {
    const trimmed = name.trim();
    if (!trimmed) return 'Bidder';
    const [first, second] = trimmed.split(' ');
    const initial = second ? `${second[0]}.` : '';
    return `${first}${initial ? ` ${initial}` : ''}***`;
  }
  if (fallbackId) {
    const suffix = fallbackId.slice(-2);
    return `Bidder ***${suffix}`;
  }
  return 'Bidder';
};

const sortBids = bids =>
  [...bids].sort((a, b) => {
    const amountDiff = Number(b.biddingAmount || 0) - Number(a.biddingAmount || 0);
    if (amountDiff !== 0) return amountDiff;
    return Number(b.biddingTime || 0) - Number(a.biddingTime || 0);
  });

export const ListingPageComponent = props => {
  const [mounted, setMounted] = useState(false);

  const [activeTab, setActiveTab] = useState('description');

  // Bid modal state
  const [isBidModalOpen, setIsBidModalOpen] = useState(false);
  const [bidModalPayload, setBidModalPayload] = useState(null);
  const [bidInput, setBidInput] = useState('');
  const [bidError, setBidError] = useState(null);
  const [bidInProgress, setBidInProgress] = useState(false);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(
    props.offerModalOpenForListingId === props.params?.id ? BooleanEnum.TRUE : BooleanEnum.FALSE
  );

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  // Countdown tick for auction
  const [nowTick, setNowTick] = useState(0);

  useEffect(() => {
    setMounted(true);

    // ✅ Run only on client
    if (typeof window !== 'undefined') {
      const checkIsMobile = () => {
        setIsMobile(window.innerWidth <= MODAL_BREAKPOINT);
      };

      // Initial check
      checkIsMobile();

      // Listen to resize
      window.addEventListener('resize', checkIsMobile);

      return () => {
        window.removeEventListener('resize', checkIsMobile);
      };
    }
  }, []);

  const {
    currentUser,
    isAuthenticated,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    history,
    callSetInitialValues,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    similarListings,
    offerModalOpenForListingId,
    bidModalOpenForListingId,
    ...restOfProps
  } = props;

  const topbar = <TopbarContainer />;

  const listingId = useMemo(() => {
    try {
      return rawParams?.id ? new UUID(rawParams.id) : null;
    } catch (e) {
      return null;
    }
  }, [rawParams?.id]);

  const isVariant = rawParams?.variant != null;
  const isPendingApprovalVariant = rawParams?.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams?.variant === LISTING_PAGE_DRAFT_VARIANT;

  const rawListing = useMemo(() => {
    if (!listingId) return null;
    if (isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly) {
      return getOwnListing(listingId);
    }
    return getListing(listingId);
  }, [
    listingId,
    isPendingApprovalVariant,
    isDraftVariant,
    showOwnListingsOnly,
    getOwnListing,
    getListing,
  ]);

  const currentListing = useMemo(() => {
    if (!rawListing) return null;
    return isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(rawListing)
      : ensureListing(rawListing);
  }, [rawListing, isPendingApprovalVariant, isDraftVariant, showOwnListingsOnly]);

  const listingSlug = useMemo(() => {
    const title = currentListing?.attributes?.title || '';
    return rawParams?.slug || createSlug(title);
  }, [rawParams?.slug, currentListing]);

  const params = useMemo(() => ({ slug: listingSlug, ...(rawParams || {}) }), [
    listingSlug,
    rawParams,
  ]);

  useEffect(() => {
    if (offerModalOpenForListingId && offerModalOpenForListingId === rawParams?.id) {
      setIsOfferModalOpen(BooleanEnum.TRUE);
    }
  }, [offerModalOpenForListingId, rawParams?.id]);

  useEffect(() => {
    if (!bidModalOpenForListingId || bidModalOpenForListingId !== rawParams?.id) {
      return;
    }
    if (!currentListing?.id) {
      return;
    }
    const publicData = currentListing?.attributes?.publicData || {};
    const minimumBidMoney = normalizeMoney(
      publicData?.minimumBid || publicData?.startingBid || publicData?.reservePrice,
      config.currency
    );
    onOpenBidModal({
      listingId: currentListing.id,
      minimumBidMoney,
      isEnded: false,
    });
  }, [bidModalOpenForListingId, rawParams?.id, currentListing?.id, config.currency]);

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isNotFound = showListingError && showListingError.status === 404;
  const isError = showListingError && !isNotFound;

  const isLoading = !showListingError && (!listingId || !currentListing || !currentListing.id);

  const isApproved =
    !!currentListing?.id && currentListing?.attributes?.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;

  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  const onTab = key => setActiveTab(key);

  const dispatch = useDispatch();

  const redirectToSignupForAction = modalType => {
    const state = { from: `${location.pathname}${location.search}${location.hash}` };
    const initialValues =
      modalType === 'bid'
        ? { bidModalOpenForListingId: rawParams?.id }
        : { offerModalOpenForListingId: rawParams?.id };
    callSetInitialValues(setInitialValues, initialValues);
    history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}), state);
  };

  const canInitiateAction = modalType => {
    if (!currentUser) {
      redirectToSignupForAction(modalType);
      return false;
    }
    if (!isUserAuthorized(currentUser)) {
      const pathParams = { missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL };
      history.push(createResourceLocatorString('NoAccessPage', routeConfiguration, pathParams, {}));
      return false;
    }
    if (!hasPermissionToInitiateTransactions(currentUser)) {
      const pathParams = { missingAccessRight: NO_ACCESS_PAGE_INITIATE_TRANSACTIONS };
      history.push(createResourceLocatorString('NoAccessPage', routeConfiguration, pathParams, {}));
      return false;
    }
    return true;
  };

  const onOpenBidModal = payload => {
    if (!canInitiateAction('bid')) {
      return;
    }
    setBidError(null);
    setBidModalPayload(payload || null);

    const minAmount = payload?.minimumBidMoney?.amount;
    if (minAmount != null && !bidInput) {
      setBidInput(String(minAmount / 100));
    }
    setIsBidModalOpen(true);
  };

  const closeBidModal = () => {
    setIsBidModalOpen(false);
    setBidModalPayload(null);
    setBidError(null);
    setBidInProgress(false);
  };

  const openOfferModal = () => {
    if (!canInitiateAction('offer')) {
      return;
    }
    setIsOfferModalOpen(BooleanEnum.TRUE);
  };

  const handleOfferModalToggle = value => {
    if (value === BooleanEnum.TRUE) {
      openOfferModal();
    } else {
      setIsOfferModalOpen(value);
    }
  };

  const handleConfirmBid = async () => {
    setBidError(null);
    const payload = bidModalPayload || {};
    const listingUUID = payload?.listingId?.uuid || currentListing?.id?.uuid;

    if (!listingUUID) {
      setBidError('Listing not ready');
      return;
    }

    const bidMoney = moneyFromBidInput(bidInput, config.currency);
    if (!bidMoney) {
      setBidError('Enter a valid bid amount');
      return;
    }

    const min = payload?.minimumBidMoney?.amount;
    if (min != null && bidMoney.amount < min) {
      setBidError(`Bid must be at least ${min / 100}`);
      return;
    }

    const amount = bidMoney.amount;
    const bidQuantity = payload?.quantity || getListingBidQuantity(currentListing) || 1;
    setBidInProgress(true);

    try {
      if (props.onPlaceBid) {
        const processAlias =
          currentListing?.attributes?.publicData?.transactionProcessAlias ||
          'default-negotiation/release-1';

        const response = await props.onPlaceBid({
          listingId: currentListing.id?.uuid,
          processAlias,
          biddingAmount: amount,
          biddingCurrency: config.currency,
          auctionEndUnix: getTimedSaleEndUnix(currentListing.attributes.publicData),
          deliveryMethod: SHIPPING,
          quantity: bidQuantity,
          bidAmount: amount,
          bidderId: currentUser?.id?.uuid,
          biddrName: userDisplayNameAsString(currentUser, ''),
          biddingTime: Math.floor(Date.now() / 1000),
        });
      }
      // ✅ success toast
      showToaster('success', 'Bid placed successfully');

      setBidInput('');
      closeBidModal();
    } catch (e) {
      const msg = e?.message || 'Failed to place bid';
      setBidError(msg);

      // ✅ error toast
      showToaster('error', msg);
    } finally {
      setBidInProgress(false);
    }
  };

  const handleOfferSubmit = async values => {
    try {
      const result = await dispatch(
        sendOffer({
          price: values?.price,
          quantity: values?.quantity,
          listing: currentListing,
        })
      );

      if (result?.id?.uuid) {
        const transactionId = result.id.uuid;

        history.push(
          createResourceLocatorString(
            'OrderDetailsPage',
            routeConfiguration,
            { id: transactionId },
            {}
          )
        );

        setTimeout(() => {
          showToaster('success', 'Offer submitted successfully');
        }, 500); // Slight delay helps toast appear after route change
      } else {
        showToaster('error', 'Transaction creation failed');
      }
    } catch (error) {
      showToaster('error', 'Error submitting offer');
    } finally {
      setIsOfferModalOpen(BooleanEnum.FALSE);
    }
  };

  const publicData = currentListing?.attributes?.publicData || {};
  const metadata = currentListing?.attributes?.metadata || {};

  const spiritTypeValue = publicData?.spirit_type;
  const spiritTypeLabel = spiritTypeValue
    ? getEnumLabelFromListingFields(
        config?.listing?.listingFields || [],
        'spirit_type',
        spiritTypeValue
      )
    : '';

  const userBids = Array.isArray(metadata?.userBids) ? metadata.userBids : [];
  const sortedBids = sortBids(userBids);
  const bidsToRender = sortedBids.map((bid, index) => {
    const bidderId = bid?.bidderId;
    const isCurrentUser = currentUser?.id?.uuid && bidderId === currentUser.id.uuid;
    const bidderLabel = isCurrentUser
      ? intl.formatMessage({ id: 'ListingPage.recentActivity.you', defaultMessage: 'You' })
      : maskBidderName(bid?.biddrName, bidderId);
    const biddingTimeUnix = Number(bid?.biddingTime);
    const biddingTimeMs = Number.isFinite(biddingTimeUnix) ? biddingTimeUnix * 1000 : null;
    const timeLabel = biddingTimeMs ? moment(biddingTimeMs).fromNow() : '-';
    const timeFullLabel = biddingTimeMs
      ? `${intl.formatDate(biddingTimeMs)} ${intl.formatTime(biddingTimeMs)}`
      : '-';

    return {
      transactionId: bid?.transactionId || index,
      bidderLabel,
      bidderId,
      amount: {
        amount: Number(bid?.biddingAmount),
        currency: bid?.biddingCurrency,
      },
      timeLabel,
      timeFullLabel,
      isCurrentUser,
      isHighest: index === 0,
    };
  });
  const bidCount = userBids.length;
  const auctionEndUnix = getTimedSaleEndUnix(publicData);
  const auctionEnabled = publicData?.isAuction === true && !!auctionEndUnix;
  const auctionActive = isActiveTimedSaleListing(publicData);

  const spiritTypeParamName = getFilterParamNameForListingField(
    config?.listing?.listingFields || [],
    'spirit_type'
  );

  useEffect(() => {
    if (!auctionActive) return;
    const id = setInterval(() => setNowTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [auctionActive]);

  const auctionEndMs = auctionEndUnix ? auctionEndUnix * 1000 : 0;
  const countdown = useMemo(() => getCountdownParts(auctionEndMs), [auctionEndMs, nowTick]);

  const endsLabel = auctionEnabled ? formatUnixToDate(auctionEndUnix, intl) : '';
  const listingAgeSummary = formatListingAge(publicData, intl, { maxUnits: 2 });

  const shortDescriptionLine = useMemo(() => {
    const bits = [];
    if (listingAgeSummary) bits.push(listingAgeSummary);
    if (publicData.spirit_type) bits.push(titleCaseFromSlug(publicData.spirit_type));
    if (publicData.currentAbv != null && publicData.currentAbv !== '')
      bits.push(`${publicData.currentAbv}% ABV`);
    return bits.join(' • ');
  }, [listingAgeSummary, publicData.spirit_type, publicData.currentAbv]);

  let pageContent = null;

  if (shouldShowPublicListingPage) {
    pageContent = <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  } else if (isNotFound) {
    pageContent = <NotFoundPage staticContext={props.staticContext} />;
  } else if (isError) {
    pageContent = <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (isLoading) {
    pageContent = <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else {
    const { description = '', price = null, title = '' } = currentListing.attributes;

    const richTitle = (
      <span>
        {richText(title, {
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
          longWordClass: css.longWord,
        })}
      </span>
    );

    const authorAvailable = currentListing && currentListing.author;
    const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
    const isOwnListing =
      userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

    const listingConfig = config.listing;
    const validListingTypes = listingConfig.listingTypes;

    const { listingType, transactionProcessAlias, unitType } = publicData || {};
    const invalidListingType = !(listingType && transactionProcessAlias && unitType);

    const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
    const showListingImage = requireListingImage(foundListingTypeConfig);

    const processName = transactionProcessAlias
      ? resolveLatestProcessName(transactionProcessAlias.split('/')[0])
      : null;

    const isBooking = processName ? isBookingProcess(processName) : false;
    const isPurchase = processName ? isPurchaseProcess(processName) : false;
    const isNegotiation = processName ? isNegotiationProcess(processName) : false;

    const processType = isBooking
      ? 'booking'
      : isPurchase
      ? 'purchase'
      : isNegotiation
      ? 'negotiation'
      : 'inquiry';

    const currentAuthor = authorAvailable ? currentListing.author : null;
    const ensuredAuthor = ensureUser(currentAuthor);

    const authorNeedsPayoutDetails =
      ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);

    const noPayoutDetailsSetWithOwnListing =
      isOwnListing && (authorNeedsPayoutDetails && !currentUser?.attributes?.stripeConnected);

    const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
      <span className={css.payoutDetailsWarning}>
        <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
        <NamedLink name="StripePayoutPage">
          <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
        </NamedLink>
      </span>
    ) : null;

    const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
    const { formattedPrice } = priceData(price, config.currency, intl);

    const commonParams = { params, history, routes: routeConfiguration };

    const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
      ...commonParams,
      getListing,
    });

    const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
      ...commonParams,
      getListing,
    });

    const onSubmit = handleSubmit({
      ...commonParams,
      currentUser,
      callSetInitialValues,
      getListing,
      onInitializeCardPaymentData,
    });

    const handleOrderSubmit = values => {
      const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
      if (isOwnListing || isCurrentlyClosed) {
        window.scrollTo(0, 0);
      } else if (isNegotiation && unitType === REQUEST) {
        onNavigateToMakeOfferPage(values);
        // onSubmit(values);
      } else if (isNegotiation && unitType === OFFER) {
        onNavigateToRequestQuotePage(values);
        // onSubmit(values);
      } else {
        onSubmit(values);
      }
    };

    const facebookImages = listingImages(currentListing, 'facebook');
    const twitterImages = listingImages(currentListing, 'twitter');
    const schemaImages = listingImages(
      currentListing,
      `${config.layout.listingImage.variantPrefix}-2x`
    ).map(img => img.url);

    const marketplaceName = config.marketplaceName;
    const schemaTitle = intl.formatMessage(
      { id: 'ListingPage.schemaTitle' },
      { title, price: formattedPrice, marketplaceName }
    );

    const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;

    const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
    const schemaAvailability = !currentListing.currentStock
      ? null
      : currentStock > 0
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';

    const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
    const noIndexMaybe =
      currentListing.attributes.state === LISTING_STATE_CLOSED ? { noIndex: true } : {};

    pageContent = (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        description={description}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        {...noIndexMaybe}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'Product',
          description: description,
          name: schemaTitle,
          image: schemaImages,
          offers: {
            '@type': 'Offer',
            url: productURL,
            ...priceForSchemaMaybe(isAuthenticated ? price : null),
            ...availabilityMaybe,
          },
        }}
      >
        <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
          <div className={css.root}>
            <div className={css.fixedWidthContainer}>
              <BidModal
                isOpen={isBidModalOpen}
                onClose={closeBidModal}
                listing={currentListing}
                bidModalPayload={bidModalPayload}
                bidInput={bidInput}
                setBidInput={setBidInput}
                bidError={bidError}
                bidInProgress={bidInProgress}
                handleConfirmBid={handleConfirmBid}
                intl={intl}
                config={config}
                onManageDisableScrolling={onManageDisableScrolling}
              />

              <Breadcrumbs
                spiritTypeValue={spiritTypeValue}
                spiritTypeLabel={spiritTypeLabel}
                spiritTypeParamName={spiritTypeParamName}
                title={title}
                intl={intl}
              />

              {invalidListingType ? (
                <ErrorPage
                  topbar={topbar}
                  scrollingDisabled={scrollingDisabled}
                  intl={intl}
                  invalidListing
                />
              ) : (
                <div className={css.contentWrapperForProductLayout}>
                  <div className={css.mainColumnForProductLayout}>
                    {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                      <ActionBarMaybe
                        className={css.actionBarForProductLayout}
                        isOwnListing={isOwnListing}
                        listing={currentListing}
                        showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                        currentUser={currentUser}
                      />
                    ) : null}

                    {mounted && currentListing.id ? (
                      <ActionBarMaybe
                        className={css.actionBarForProductLayout}
                        isOwnListing={isOwnListing}
                        listing={currentListing}
                        currentUser={currentUser}
                        editParams={{
                          id: listingId?.uuid,
                          slug: listingSlug,
                          type: listingPathParamType,
                          tab: listingTab,
                        }}
                      />
                    ) : null}

                    {showListingImage ? (
                      <SectionGallery
                        listing={currentListing}
                        variantPrefix={config.layout.listingImage.variantPrefix}
                      />
                    ) : null}

                    <div className={css.contentContainer}>
                      <div
                        className={
                          showListingImage ? css.mobileHeading : css.noListingImageHeadingProduct
                        }
                      >
                        {showListingImage ? (
                          <H4 as="h1" className={css.orderPanelTitle}>
                            <FormattedMessage
                              id="ListingPage.orderTitle"
                              values={{ title: richTitle }}
                            />{' '}
                          </H4>
                        ) : (
                          <H3 as="h1" className={css.orderPanelTitle}>
                            <FormattedMessage
                              id="ListingPage.orderTitle"
                              values={{ title: richTitle }}
                            />{' '}
                          </H3>
                        )}
                      </div>

                      {isMobile ? (
                        <OrderPanelCtaInfo
                          intl={intl}
                          auctionEnabled={auctionEnabled}
                          countdown={countdown}
                          endsLabel={endsLabel}
                          shortDescriptionLine={shortDescriptionLine}
                          showLiveAuction={auctionActive}
                          showVerified
                          listing={currentListing}
                          userBids={userBids}
                          price={price}
                          publicData={publicData}
                          validListingTypes={validListingTypes}
                          marketplaceCurrency={config.currency}
                          showSellerCommission={isOwnListing}
                          PriceMaybeComponent={PriceMaybe}
                        />
                      ) : null}

                      <ContentTabs
                        activeTab={activeTab}
                        onTab={onTab}
                        title={title}
                        publicData={publicData}
                        description={description}
                        currentUser={currentUser}
                        bidCount={bidCount}
                        intl={intl}
                        listingFieldsConfig={config?.listing?.listingFields || []}
                        bidsToRender={bidsToRender}
                        auctionEnabled={auctionEnabled}
                      />
                    </div>
                  </div>

                  <div className={css.orderColumnForProductLayout}>
                    {auctionEnabled ? (
                      <AuctionBuyBox
                        listing={currentListing}
                        userBids={userBids}
                        publicData={publicData}
                        marketplaceCurrency={config.currency}
                        currentUser={currentUser}
                        intl={intl}
                        onPlaceBid={onOpenBidModal}
                        isBidDisabled={isOwnListing || !auctionActive || countdown.isEnded}
                        showSellerCommission={isOwnListing}
                        onManageDisableScrolling={onManageDisableScrolling}
                      />
                    ) : (
                      <OrderPanel
                        className={classNames(css.productOrderPanel, {
                          [css.imagesEnabled]: showListingImage,
                        })}
                        listing={currentListing}
                        isOwnListing={isOwnListing}
                        onSubmit={handleOrderSubmit}
                        onOpenBidModal={onOpenBidModal}
                        isListingPage
                        title={
                          <FormattedMessage
                            id="ListingPage.orderTitle"
                            values={{ title: richTitle }}
                          />
                        }
                        titleDesktop={
                          <FormattedMessage
                            id="ListingPage.orderTitle"
                            values={{ title: richTitle }}
                          />
                        }
                        payoutDetailsWarning={payoutDetailsWarning}
                        onManageDisableScrolling={onManageDisableScrolling}
                        {...restOfProps}
                        validListingTypes={config.listing.listingTypes}
                        marketplaceCurrency={config.currency}
                        dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                        marketplaceName={config.marketplaceName}
                        showListingImage={showListingImage}
                        setIsOfferModalOpen={handleOfferModalToggle}
                      />
                    )}
                  </div>
                </div>
              )}

              <SimilarListings similarListings={similarListings} intl={intl} />
            </div>
            <OfferModal
              isOpen={isOfferModalOpen}
              onClose={() => handleOfferModalToggle(BooleanEnum.FALSE)}
              title={title}
              authorDisplayName={authorDisplayName}
              listing={currentListing}
              onSubmit={handleOfferSubmit}
            />
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  return pageContent;
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;

  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    offerModalOpenForListingId,
    bidModalOpenForListingId,
    similarListings,
  } = state.ListingPage;

  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    offerModalOpenForListingId,
    bidModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    similarListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValuesFn, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
  onPlaceBid: params => dispatch(submitBidTransaction(params)),
});

const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
